import { Action } from "@cervval.js/common-action/src/action.model";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools";
import { CjsDayAgendaColumnView } from "../dayAgendaColumnView/dayAgendaColumnView";
import { ColumnUid } from "./dayAgenda.types";
import { HandleType } from "./dayAgendaInteractions.helper";
import { DayAgendaSelectedData } from "./dayAgendaSelection.helper";

//#region Agenda item interaction events

/** Event triggered by a click on a agenda item */
export type ItemClickEvent<ItemObject> = {

    /** HTML item top position relative to the col container */
    itemTop: string,

    /** HTML item height */
    itemHeight: string,

    /** Item start timestamp */
    startTs: UnixTimestamp,

    /** Item finish timestamp */
    finishTs: UnixTimestamp,

    /** Target col uid */
    colUid: string,

    /** Agenda item clicked on */
    item: ItemObject,

    /** HTML item clicked on */
    htmlItem: HTMLElement,

    /** Original click event */
    originalClickEvent: MouseEvent
}

/** Event triggered by a agenda item creation */
export type ItemCreationFinishEvent<ItemObject> = {

    /** Newly created items */
    items: ItemObject | ItemObject[],

    /** Original click event */
    clickEvent?: Event
}

/** Event triggered by a agenda item removal */
export type ItemRemoveFinishEvent<ItemObject> = {
    /** Removed data */
    removedData: DayAgendaSelectedData<ItemObject>[]
}

/** Event triggered by a agenda item resizing */
export type ItemResizeFinishEvent<ItemObject> = {

    /** Handle type used to resize the item */
    handleType: HandleType,

    /** HTML item left position relative to the col container BEFORE resize */
    oldItemTop: string,

    /** HTML item width BEFORE resize */
    oldItemHeight: string,

    /** HTML item left position relative to the col container AFTER resize */
    newItemTop: string,

    /** HTML item width AFTER resize */
    newItemHeight: string,

    /** New item start timestamp (after resize) */
    newStartTs: UnixTimestamp,

    /** New item finish timestamp (after resize) */
    newFinishTs: UnixTimestamp,

    /** Agenda item resized */
    item: ItemObject,

    /** HTML item resized */
    htmlItem: HTMLElement
}

/** Event triggered by a agenda item resizing */
export type ItemResizeDragEvent<ItemObject> = {

    /** Handle type used to resize the item */
    handleType: HandleType,

    /** item width BEFORE resize */
    oldItemHeight: number,

    /** item width ON CURRENT resize */
    newItemHeight: number,

    /** New item start timestamp (on current resize) */
    newStartTs: UnixTimestamp,

    /** New item finish timestamp (on current resize) */
    newFinishTs: UnixTimestamp,

    /** Agenda item resized */
    item: ItemObject,

    /** HTML item resized */
    htmlItem: HTMLElement
}

/** Event triggered by a agenda item drag */
export type ItemDragEvent<ItemObject> = {

    /** HTML item left position relative to the col container */
    itemTop: string,

    /** HTML item width */
    itemHeight: string,

    /** Item start timestamp */
    startTs: UnixTimestamp,

    /** Item finish timestamp */
    finishTs: UnixTimestamp,

    /** Target col uid */
    columnUid: ColumnUid,

    /** Agenda item dragged */
    item: ItemObject,

    /** HTML item dragged */
    htmlItem: HTMLElement
}

/** Event triggered by a agenda item drop */
export type ItemDropEvent<ItemObject, ElementTypes = unknown> = {

    /** Persistence action */
    action: Action<ElementTypes>,

    /** HTML item left position relative to the col container */
    itemTop: string,

    /** HTML item width */
    itemHeight: string,

    /** Item start timestamp */
    startTs: UnixTimestamp,

    /** Item finish timestamp */
    finishTs: UnixTimestamp,

    /** Origin col uid */
    oldColUid: string | null,

    /** Target col uid */
    newColUid: string | null,

    /** Agenda item dropped */
    item: ItemObject,

    /** HTML item dropped */
    htmlItem: HTMLElement,

    /** HTML item X position has been reset */
    resetPositionX: boolean,

    /** HTML item Y position has been reset */
    resetPositionY: boolean,

    /** Original mouse event */
    originalMouseEvent: MouseEvent,

    /** Mouse up drop timestamp */
    tsDrop: UnixTimestamp
}


//#endregion Agenda item interaction events


export enum DayAgendaRefreshReason {
    SET_DATA = "setData",
    REFRESH = "refresh",
    RESIZE = "resize",

    ITEM_RESIZE = "item.resize",
    ITEM_MOVE = "item.move",
    ITEM_DELETE = "item.delete",
    ITEM_CREATE = "item.create",
    ITEM_BULK_DELETE = "item.bulkDelete",

    PAN = "interaction_pan",
    ZOOM = "interaction_zoom",
    NAVIGATE = "interaction_navigate",
    PAN_FINISH = "interaction_pan_finish",
    ZOOM_FINISH = "interaction_zoom_finish",
    NAVIGATE_FINISH = "interaction_navigate_finish"
}

export const INTERACTION_REFRESH_REASON_PREFIX = "interaction_";
/** @returns the AgendaRefreshReason for the interaction event */
export function getInteractionRefreshReason(event: DayAgendaInteraction | undefined, options: { isFinish: boolean }): DayAgendaRefreshReason {
    return INTERACTION_REFRESH_REASON_PREFIX + (event ?? "unknown") + (options.isFinish ? "_finish" : "") as DayAgendaRefreshReason;
}

/** @returns true if reason relates to an interaction */
export function isInteractionRefreshReason(reason: string | undefined): boolean {
    return reason != null && reason.startsWith(INTERACTION_REFRESH_REASON_PREFIX);
}

export enum DayAgendaInteraction {
    PAN = "pan",
    ZOOM = "zoom",
    NAVIGATE = "navigate"
}

export enum ItemInteraction {
    RESIZE = "resize",
    MOVE = "move",
    DELETE = "delete",
    CREATE = "create",
    BULK_DELETE = "bulkDelete"
}

/** Event triggered by a agenda row container mouse down */
export type ColumnContainerMouseDownEvent<ItemObject, ColumnObject> = {

    /** Click position on the X axis in px and relative to the row container */
    clickPositionY: number,

    /** Timestamp corresponding to the click */
    ts: UnixTimestamp,

    /** Row clicked */
    agendaColumnView?: CjsDayAgendaColumnView<ItemObject, ColumnObject>,

    /** Original click event */
    originalClickEvent: Event
}


/** Event triggered by a agenda item click */
export type RowClickEvent<ItemObject, ColumnObject> = {

    /** Click position on the X axis in px and relative to the row container */
    clickPositionY: number,

    /** Timestamp corresponding to the click */
    ts: UnixTimestamp,

    /** Row clicked */
    agendaColumnView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,

    /** Item if clicked */
    item?: ItemObject,

    /** Original click event */
    originalClickEvent: Event
}
/** Event triggered by a agenda item drop */
export type ColumnMouseUpEvent<ItemObject, ColumnObject> = {

    /** Mouse up position on the Y axis in px and relative to the row container */
    mouseUpPositionY: number,

    /** Timestamp corresponding to the mouse up event */
    ts: UnixTimestamp,

    /** Row under the mouse up triggered position */
    agendaColumnView: CjsDayAgendaColumnView<ItemObject, ColumnObject>,

    /** Original mouse up event */
    originalClickEvent: Event,

    /** HTML Element corresponding to the tmp created item drawn or to the dropped item. */
    htmlItem?: HTMLElement,

    /** clicked item object */
    item: ItemObject | undefined
}
